import * as React from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SendIcon from "@mui/icons-material/Send";
import { KitchenArrivalRow, PbvRally } from "./RallyBoardPopup";
import { AiTurn } from "./ActiveRallyVideo";

// keep this type small + compatible with what your API returns
// export type AiTurn = {
//   id: string;
//   createdAtIso?: string | null;
//   question?: string | null;
//   answerMd?: string | null;

//   // allow older naming just in case
//   prompt?: string | null;
//   responseMd?: string | null;
// };

function bestQuestion(t: AiTurn) {
  return (t.question ?? t.question ?? "").trim();
}
// function bestAnswerMd(t: AiTurn) {
//   return (t.answerMd ?? t.responseMd ?? "").trim();
// }

function bestAnswerMd(t: any): string {
  if (!t) return "";

  if (t?.status === "error" && typeof t?.error === "string" && t.error.trim()) {
    return t.error.trim();
  }

  if (typeof t.responseText === "string" && t.responseText.trim()) {
    return t.responseText.trim();
  }

  const r = t.responseJson ?? null;
  if (r && typeof r === "object") {
    if (typeof r.answerToQuestion === "string" && r.answerToQuestion.trim()) {
      return r.answerToQuestion.trim();
    }

    if (
      typeof r?.rallyOverview?.headline === "string" &&
      r.rallyOverview.headline.trim()
    ) {
      return r.rallyOverview.headline.trim();
    }

    if (
      typeof r?.rallyOverview?.keyTurningPoint === "string" &&
      r.rallyOverview.keyTurningPoint.trim()
    ) {
      return `Turning point: ${r.rallyOverview.keyTurningPoint.trim()}`;
    }
  }

  if (typeof t.answer === "string" && t.answer.trim()) {
    return t.answer.trim();
  }

  return "";
}

export function AskAiPanel(props: {
  noTitleStuff?: boolean;
  packageId: string;
  row: {
    rallyIndex: number;
    segmentStartMs: number;
    segmentEndMs: number;
  } | null;
  turns: AiTurn[];
  onAskAi?: (args: {
    row: {
      rallyIndex: number;
      segmentStartMs: number;
      segmentEndMs: number;
    };
    question: string;
  }) => Promise<void> | void;
  onDeleteTurn: (t: AiTurn) => Promise<void> | void;
}) {
  const { row, turns, onAskAi, onDeleteTurn, noTitleStuff } = props;

  const [question, setQuestion] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  // reset prompt when switching rallies (feels nicer in this popup)
  React.useEffect(() => {
    setQuestion("");
    setBusy(false);
  }, [row?.rallyIndex, row?.segmentStartMs, row?.segmentEndMs]);

  const canAsk = Boolean(row) && question.trim().length > 0 && !busy;
  const canSubmit = Boolean(row) && question.trim().length > 0;

  const quick = [
    "What decided this rally?",
    // "Any footwork or positioning issue?",
    // "Was the 3rd/5th shot decision good?",
    "Any missed opportunities?",
  ];

  return (
    <Box
      sx={{
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 2,
        p: 1.2,
        height: "100%",
        minHeight: 220,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {noTitleStuff ? null : (
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Typography sx={{ fontWeight: 900 }}>Ask AI</Typography>
          {row ? (
            <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
              Rally {row.rallyIndex}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
              Pick a rally
            </Typography>
          )}
        </Box>
      )}
      {/* <TextField
        size="small"
        placeholder="Ask something about this rally…"
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        disabled={!row || busy}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            if (!canAsk || !row) return;
            setBusy(true);
            Promise.resolve(onAskAi({ row, question: question.trim() }))
              .finally(() => setBusy(false));
          }
        }}
        helperText={
          <span style={{ opacity: 0.75 }}>
            Tip: Cmd/Ctrl+Enter to send
          </span>
        }
      /> */}

      <Box
        sx={{
          border: row
            ? "1px solid rgba(80,160,255,0.55)"
            : "1px solid rgba(255,255,255,0.12)",
          borderRadius: 2,
          p: 1,
          background: row ? "rgba(40,90,140,0.14)" : "rgba(255,255,255,0.03)",
          boxShadow: row
            ? "0 0 0 1px rgba(80,160,255,0.20), 0 10px 24px rgba(0,0,0,0.35)"
            : "none",
          transition:
            "border-color 120ms ease, background 120ms ease, box-shadow 120ms ease",
        }}
      >
        <TextField
          data-demo="ask-ai-question"
          size="small"
          fullWidth
          multiline
          minRows={3}
          placeholder={
            row
              ? "Ask what happened… (e.g., “Why did we lose this rally?”)"
              : "Pick a rally to ask AI…"
          }
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          disabled={!row || busy}
          autoComplete="off"
          // ✅ this is the browser underline killer in React
          spellCheck={false}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              if (!row || !onAskAi || busy || question.trim().length === 0)
                return;
              setBusy(true);
              Promise.resolve(onAskAi({ row, question: question.trim() }))
                .then(() => setQuestion(""))
                .finally(() => setBusy(false));
            }
          }}
          // onKeyDown={(e) => {
          //   if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
          //     e.preventDefault();
          //     if (!canAsk || !row) return;
          //     if (!onAskAi) return;
          //     setBusy(true);
          //     Promise.resolve(
          //       onAskAi({ row, question: question.trim() }),
          //     ).finally(() => setBusy(false));
          //   }
          // }}
          // ✅ MUI v6+ (InputProps is deprecated): push attrs to the actual textarea/input
          slotProps={{
            htmlInput: {
              spellCheck: false,
              autoCorrect: "off",
              autoCapitalize: "off",
            },
          }}
          // keep your styling, but move from InputProps -> sx targeting the input root
          sx={{
            "& .MuiOutlinedInput-root": {
              background: "rgba(0,0,0,0.18)",
              borderRadius: 2,
              color: "white",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: row
                ? "rgba(120,190,255,0.70)"
                : "rgba(255,255,255,0.18)",
            },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: row
                ? "rgba(160,220,255,0.95)"
                : "rgba(255,255,255,0.26)",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                borderColor: "rgba(160,220,255,1)",
              },
          }}
          helperText={
            <span style={{ opacity: 0.85, color: "white" }}>
              {row
                ? "Tip: Cmd/Ctrl+Enter to send"
                : "Select a rally above to enable asking"}
            </span>
          }
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0,
            mt: 0,
          }}
        >
          {noTitleStuff ? null : (
            <Typography sx={{ fontSize: 12, opacity: row ? 0.95 : 0.65 }}>
              {row ? "Ask AI about this rally" : "Ask AI"}
            </Typography>
          )}

          <Button
            size="small"
            variant="contained"
            data-demo="ask-ai-submit"
            // startIcon={<SendIcon />}
            startIcon={!busy ? <SendIcon /> : undefined}
            //disabled={!canAsk}
            disabled={!canSubmit || !onAskAi}
            onClick={() => {
              if (!row || !onAskAi || busy) return;
              setBusy(true);
              if (!onAskAi) return;
              Promise.resolve(onAskAi({ row, question: question.trim() }))
                .then(() => setQuestion(""))
                .finally(() => setBusy(false));
            }}
            sx={{
              fontWeight: 900,
              borderRadius: 2,
              px: 1.5,
              boxShadow: row ? "0 10px 22px rgba(0,0,0,0.35)" : "none",
              // make CTA feel more “primary”
              // "&.Mui-disabled": {
              //   opacity: 0.45,
              // },
              "&.Mui-disabled": {
                opacity: 0.55,
                color: "rgba(255,255,255,0.9)",
                background: "rgba(25,118,210,0.45)",
              },
            }}
          >
            {busy ? "Asking…" : "Ask"}
          </Button>

          {busy ? (
            <Box sx={{ mt: 0.9 }}>
              <LinearProgress
                sx={{
                  height: 6,
                  borderRadius: 999,
                  backgroundColor: "rgba(255,255,255,0.10)",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 999,
                  },
                }}
              />
            </Box>
          ) : null}
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
        {quick.map((q) => (
          <Button
            key={q}
            size="small"
            variant="outlined"
            onClick={() => setQuestion(q)}
            disabled={!row || busy}
            sx={{ textTransform: "none" }}
          >
            {q}
          </Button>
        ))}
      </Box>

      {/* <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          size="small"
          variant="contained"
          startIcon={<SendIcon />}
          disabled={!canAsk}
          onClick={() => {
            if (!row) return;
            setBusy(true);
            Promise.resolve(onAskAi({ row, question: question.trim() }))
              .then(() => setQuestion(""))
              .finally(() => setBusy(false));
          }}
        >
          {busy ? "Asking…" : "Ask"}
        </Button>
      </Box> */}

      {/* <Divider sx={{ opacity: 0.25 }} /> */}

      <Box sx={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", pr: 0.5 }}>
        {!turns?.length ? (
          <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
            No AI turns yet for this clip.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {turns.map((t) => {
              const q = bestQuestion(t);
              const a = bestAnswerMd(t);
              return (
                <Box
                  key={t.id}
                  data-demo="ask-ai-turn"
                  sx={{
                    border: "1px solid rgba(255,255,255,0.10)",
                    borderRadius: 2,
                    p: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography sx={{ fontWeight: 900, fontSize: 12 }}>
                      {q || "(no question)"}
                    </Typography>

                    <Tooltip title="Delete turn">
                      <IconButton
                        size="small"
                        onClick={() => row && onDeleteTurn(t)}
                        sx={{
                          color: "red",
                          opacity: 0.75,
                          "&:hover": { opacity: 1 },
                        }}
                      >
                        <DeleteOutlineIcon color="red" fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {a ? (
                    <Typography
                      sx={{
                        fontSize: 12,
                        opacity: 0.85,
                        whiteSpace: "pre-wrap",
                        mt: 0.5,
                      }}
                    >
                      {a}
                    </Typography>
                  ) : (
                    <Typography sx={{ fontSize: 12, opacity: 0.6, mt: 0.5 }}>
                      (no response)
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}
